import React, { createContext, useEffect, useMemo, useState } from "react";
import siteConfig from "../config/siteConfig";

export const CartContext = createContext();

const STORAGE_KEYS = {
  cart: `${siteConfig.brandName}.cart`,
  wishlist: `${siteConfig.brandName}.wishlist`,
};

const readStorage = (key, fallback) => {
  if (typeof window === "undefined") return fallback;
  try {
    const value = window.localStorage.getItem(key);
    const parsed = value ? JSON.parse(value) : fallback;
    return Array.isArray(parsed) ? parsed : fallback;
  } catch {
    return fallback;
  }
};

const writeStorage = (key, value) => {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Storage can be unavailable in private browsing. State still works in-memory.
  }
};

export const CartProvider = ({ children }) => {
  const [cartItems, setCartItems] = useState(() =>
    readStorage(STORAGE_KEYS.cart, [])
      .filter((item) => item && Number.isFinite(Number(item.id)))
      .map((item) => ({
        id: Number(item.id),
        size: String(item.size || ""),
        color: String(item.color || ""),
        quantity: Math.max(1, Math.min(10, Number(item.quantity) || 1)),
      }))
  );
  const [wishlistItems, setWishlistItems] = useState(() =>
    readStorage(STORAGE_KEYS.wishlist, [])
      .map(Number)
      .filter(Number.isFinite)
  );

  useEffect(() => writeStorage(STORAGE_KEYS.cart, cartItems), [cartItems]);
  useEffect(
    () => writeStorage(STORAGE_KEYS.wishlist, wishlistItems),
    [wishlistItems]
  );

  const addToCart = (productId, quantity = 1, size = "", color = "") => {
    const nextQuantity = Math.max(1, Math.min(10, Number(quantity) || 1));
    setCartItems((items) => {
      const matchingItem = items.find(
        (item) =>
          item.id === productId && item.size === size && item.color === color
      );

      if (!matchingItem) {
        return [
          ...items,
          { id: productId, size, color, quantity: nextQuantity },
        ];
      }

      return items.map((item) =>
        item.id === productId && item.size === size && item.color === color
          ? { ...item, quantity: Math.min(10, item.quantity + nextQuantity) }
          : item
      );
    });
  };

  const removeFromCart = (productId, size, color) => {
    setCartItems((items) =>
      items.filter(
        (item) =>
          !(item.id === productId && item.size === size && item.color === color)
      )
    );
  };

  const updateQuantity = (productId, size, color, quantity) => {
    const nextQuantity = Math.max(1, Math.min(10, Number(quantity) || 1));
    setCartItems((items) =>
      items.map((item) =>
        item.id === productId && item.size === size && item.color === color
          ? { ...item, quantity: nextQuantity }
          : item
      )
    );
  };

  const clearCart = () => setCartItems([]);

  const addWishlist = (productId) =>
    setWishlistItems((items) =>
      items.includes(productId) ? items : [...items, productId]
    );

  const removeWishlist = (productId) =>
    setWishlistItems((items) => items.filter((id) => id !== productId));

  const toggleWishlist = (productId) =>
    setWishlistItems((items) =>
      items.includes(productId)
        ? items.filter((id) => id !== productId)
        : [...items, productId]
    );

  const cartCount = useMemo(
    () => cartItems.reduce((total, item) => total + item.quantity, 0),
    [cartItems]
  );

  const value = {
    cartItems,
    wishlistItems,
    cartCount,
    addToCart,
    removeFromCart,
    updateQuantity,
    clearCart,
    addWishlist,
    removeWishlist,
    toggleWishlist,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};
